import axiosInstance from "../axiosInstance";

export interface MaintStation {
  id: number;
  charging_station_guid: string;
  name: string;
  text_address: string | null;
  org_id: number;
  org_name: string;
  org_guid: string;
  onboard_date: string | null;
  location_lat: number;
  location_lon: number;
  station_logo_url: string | null;
  is_active: boolean | null;
  charging_station_type_id: number | null;
  station_type: string | null;
  gst_state_code: number | null;
  gst_state_name: string | null;
  device_count?: number;
  active_devices?: number;
}

export interface GstStateItem {
  state_code: number;
  state_name: string;
  state_short: string;
}

export interface MaintDevice {
  id: number;
  unique_name: string | null;
  display_name: string | null;
  device_logo_url: string | null;
  device_guid: string | null;
  ocpp_device_id: string | null;
  ocpp_version: string | null;
  org_id: number | null;
  org_name: string | null;
  org_guid: string | null;
  station_name: string | null;
  station_guid: string | null;
  last_active: string | null;
  device_status: string;
}

export interface MaintConnector {
  id: number;
  connector_name: string;
  socket_type: string | null;
  status: string | null;
  capacity_kwh: number | null;
}

export interface MaintDeviceDetail extends MaintDevice {
  station_id: number | null;
  station_position: number | null;
  web_socker_add: string | null;
  connectors: MaintConnector[];
}

export interface OrgItem {
  id: number;
  name: string;
  org_guid: string;
}

export interface StationTypeItem {
  id: number;
  name: string;
}

export interface SocketTypeItem {
  id: number;
  name: string;
}

export interface ConnectorStatusItem {
  id: number;
  name: string;
}

export interface MaintStationDevice {
  id: number;
  unique_name: string | null;
  display_name: string | null;
  device_logo_url: string | null;
  device_guid: string | null;
  ocpp_device_id: string | null;
  station_position: number | null;
  last_active: string | null;
  device_status: string;
}

export interface AuditLogEntry {
  id: number;
  action: string;
  entity_type: string;
  entity_id: string;
  changes: Record<string, any> | null;
  performed_by: string;
  performed_by_email: string | null;
  performed_at: string;
  ip_address: string | null;
  notes: string | null;
}

export interface TaxComponentItem {
  id: number;
  service_type_key: string;
  tax_name: string;
  display_name: string;
  tax_percentage: number;
  is_active: boolean;
  supply_type: string;
}

export interface ServiceTypeWithTaxes {
  id: number;
  service_type_key: string;
  tax_model: string;
  is_active: boolean;
  tax_components: TaxComponentItem[];
}

export interface PricingOverrideItem {
  id: number;
  org_id: number;
  org_name: string;
  org_guid: string;
  charging_station_id: number;
  station_name: string;
  station_guid: string | null;
  charger_type: string;
  price_per_kwh: number;
  valid_from: string;
  valid_thru: string;
  is_default: boolean;
  priority: number;
  service_type_key: string | null;
  is_active: boolean;
}

export interface PricingOverrideCreate {
  org_id: number;
  charging_station_id: number;
  charger_type: string;
  price_per_kwh: number;
  valid_from: string;
  valid_thru: string;
  is_default: boolean;
  priority: number;
  service_type_key: string;
}

export interface DeviceRateItem {
  id: number;
  device_id: number;
  rate_kwh: number | null;
  service_type_key: string | null;
  unique_name: string | null;
  display_name: string | null;
}

export interface StationDropdownItem {
  id: number;
  name: string;
  station_guid: string;
  org_name: string;
}

// ── Vehicles (cross-tenant master data) ──────────────────────────────────
export interface VehicleCompany {
  id: number;
  name: string;
  company_guid: string;
  model_count: number;
}

export interface VehicleModel {
  id: number;
  vehicle_company_id: number;
  company_name: string;
  model_name: string;
  vehicle_type: number;
  vehicle_type_name: string | null;
  battery_chemistry: string;
  rated_capacity_kwh: number;
  claimed_full_charge_range: number;
  charging_socket_type_id: number;
  socket_name: string | null;
  vehicle_model_guid: string;
  user_vehicle_count: number;
}

export interface VehicleTypeOption {
  id: number;
  type_name: string;
}

export interface VehicleSocketType {
  id: number;
  socket_name: string;
  charging_current_mode: string | null;
}

export interface VehicleCompanyOption {
  id: number;
  name: string;
}

export interface VehicleModelCreate {
  vehicle_company_id: number;
  model_name: string;
  vehicle_type: number;
  battery_chemistry: string;
  rated_capacity_kwh: number;
  claimed_full_charge_range: number;
  charging_socket_type_id: number;
}

// ── Utilization (cross-tenant, excludes 'Test Private') ──────────────────
export interface UtilWindow {
  start: string;
  end: string;
  range_hours: number;
}

export interface UtilOverview {
  window: UtilWindow;
  orgs: number;
  stations: number;
  devices: number;
  connectors: number;
  connectors_with_capacity: number;
  capacity_coverage_pct: number;
  sessions: number;
  energy_kwh: number;
  active_hours: number;
  in_service_connector_hours: number;
  occupancy_pct: number | null;
  throughput_pct: number | null;
  revenue: number;
  avg_power_kw: number | null;
}

export interface UtilDailyPoint {
  day: string;
  sessions: number;
  energy_kwh: number;
  active_hours: number;
  revenue: number;
}

export interface UtilOverviewResponse {
  overview: UtilOverview;
  daily: UtilDailyPoint[];
}

export interface StationUtil {
  station_id: number;
  station_guid: string;
  station_name: string;
  org_name: string;
  sessions: number;
  energy_kwh: number;
  active_hours: number;
  connectors: number;
  occupancy_pct: number | null;
  throughput_pct: number | null;
  revenue: number;
  avg_power_kw: number | null;
}

export interface DeviceUtil {
  device_id: number;
  device_name: string | null;
  display_name: string | null;
  sessions: number;
  energy_kwh: number;
  active_hours: number;
  connectors: number;
  occupancy_pct: number | null;
  throughput_pct: number | null;
  revenue: number;
  avg_power_kw: number | null;
}

export interface ConnectorUtil {
  connector_id: number;
  connector_name: string;
  socket_name: string | null;
  capacity_kwh: number | null;
  sessions: number;
  energy_kwh: number;
  active_hours: number;
  occupancy_pct: number | null;
  throughput_pct: number | null;
  revenue: number;
  avg_power_kw: number | null;
}

/** Effective uptime over a window (device_connector_status_log). uptime_pct = reachable;
 *  effective_healthy_pct additionally excludes faulted/unavailable. Hours connector-weighted. */
export interface StationUptime {
  station_guid: string;
  station_name: string;
  connectors: number;
  considered_hours: number;
  offline_hours: number;
  faulted_hours: number;
  uptime_pct: number | null;
  effective_healthy_pct: number | null;
}

export interface ServiceHealthSummary {
  stations: number;
  connectors: number;
  considered_hours: number;
  offline_hours: number;
  faulted_hours: number;
  uptime_pct: number | null;
  effective_healthy_pct: number | null;
}

export interface ServiceHealth {
  window: UtilWindow;
  summary: ServiceHealthSummary;
  stations: StationUptime[];
}

// Health monitor (session-outcome health — migrated Redash Q29/Q30/Q48)
export interface StationHealthRow {
  station_guid: string; station: string; org_name: string | null; is_active: boolean | null;
  sessions: number; incomplete: number; zero_energy: number; last_session: string | null; health_status: string;
}
export interface DeviceHealthRow {
  device_id: number; device_name: string | null; ocpp_version: string | null; org_name: string | null;
  station: string | null; sessions: number; incomplete: number; zero_energy: number;
  last_session: string | null; health_status: string;
}
export interface UnderperfConnectorRow {
  connector_id: number; connector_name: string | null; device_name: string | null; station: string | null;
  org_name: string | null; current_status: string | null; sessions_window: number;
  last_session: string | null; health_flag: string;
}
export interface HealthMonitor {
  window: UtilWindow;
  stations: StationHealthRow[];
  devices: DeviceHealthRow[];
  connectors: UnderperfConnectorRow[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DeviceCreatePayload {
  unique_name: string;
  web_socker_add: string;
  device_id: string;
  ocpp_version?: string;
  device_logo_url?: string;
  display_name?: string;
  station_id?: number;
  station_position?: number;
  connectors: { connector_name: string; socket_type_id?: number; capacity_kwh?: number }[];
}

export interface StationSettings {
  charging_station_guid: string;
  auto_restart_enabled: boolean;
  stale_timeout_minutes: number;
  auto_restart_hold_minutes: number;
}

const BASE = "/api/maintenance";


// ============================================================================
// Procurement — device pool, orders, goods-receipts, pool-first onboarding
// ============================================================================
export type ProcStage = "generated" | "dispatched" | "received" | "onboarded" | "retired";
export type ProcCondition = "ok" | "damaged" | "doa" | "wrong_item";
export type OrderEffStatus = "draft" | "issued" | "partial" | "closed" | "cancelled";

export interface ProcManufacturer {
  id: number;
  name: string;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  gstin: string | null;
  notes: string | null;
  is_active: boolean;
  units_ordered: number;
  created_at: string | null;
}

export interface ProcBatch {
  id: number;
  name: string;
  manufacturer_id: number | null;
  manufacturer_name: string | null;
  hardware_model: string | null;
  po_ref: string | null;
  connector_std: string | null;
  power_kw: number | null;
  unit_cost: number | null;
  qty: number;
  notes: string | null;
  minted_on: string | null;
  generated: number;
  dispatched: number;
  received: number;
  onboarded: number;
  retired: number;
}

export interface ProcDevice {
  id: number;
  unique_id: string;
  qr_url: string;
  ws_url: string;
  stage: ProcStage;
  batch_id: number | null;
  batch_name: string | null;
  manufacturer_name: string | null;
  cp_vendor: string | null;
  cp_model: string | null;
  serial_no: string | null;
  firmware: string | null;
  condition: ProcCondition | null;
  dispatched_on: string | null;
  received_on: string | null;
  devices_main_id: number | null;
  order_no: string | null;
  station_name: string | null;
  display_name: string | null;
  connectivity: Connectivity;
  sim_id: number | null;
  sim_msisdn: string | null;
  sim_valid_until: string | null;
  sim_days_left: number | null;
}

export interface ProcDeviceDetail extends ProcDevice {
  charge_box_serial: string | null;
  iccid: string | null;
  imsi: string | null;
  meter_type: string | null;
  meter_serial: string | null;
  first_boot_at: string | null;
  warranty_start: string | null;
  warranty_end: string | null;
  bis_cert: string | null;
  arai_cert: string | null;
  meter_cal_expiry: string | null;
  evyatra_id: string | null;
  install_site: string | null;
  technician: string | null;
  replaced_by_id: number | null;
  replaced_by_unique_id: string | null;
  retire_reason: string | null;
  notes: string | null;
  po_ref: string | null;
  created_at: string | null;
}

export interface ProcPoolSummary {
  generated: number;
  dispatched: number;
  received: number;
  onboarded: number;
  retired: number;
  total: number;
}

export interface ProcOrderLineItem {
  procurement_device_id: number;
  unique_id: string;
  stage: ProcStage;
  condition: ProcCondition | null;   // null => still outstanding
  receipt_no: string | null;
}

/** A file generated for an order and kept. The sha256 is the point: the
 *  delivery log says a file was sent, this says which bytes they were. */
export interface ProcArtifact {
  id: number;
  order_id: number;
  kind: "devices_csv" | "labels_pdf";
  object_key: string;
  content_type: string;
  byte_size: number;
  sha256: string;
  device_count: number | null;
  generated_by: string;
  generated_at: string;
}

export interface ProcDelivery {
  id: number;
  channel: "email" | "download" | "manual";
  target: string | null;
  status: "ok" | "sent" | "failed";
  error: string | null;
  attempted_by: string | null;
  attempted_at: string | null;
}

export interface ProcReceiptSummary {
  id: number;
  receipt_no: string;
  received_on: string;
  challan_ref: string | null;
  note: string | null;
  received_by: string | null;
  ok_count: number;
  bad_count: number;
}

export interface ProcOrder {
  id: number;
  order_no: string;
  manufacturer_id: number;
  manufacturer_name: string | null;
  status: "draft" | "issued" | "cancelled";
  effective_status: OrderEffStatus;
  note: string | null;
  total_items: number;
  ok_count: number;
  damaged_count: number;
  outstanding_count: number;
  delivery_count: number;
  last_delivery_status: string | null;
  created_at: string | null;
  issued_on: string | null;
  closed_on: string | null;
}

export interface ProcOrderDetail extends ProcOrder {
  manufacturer_email: string | null;
  items: ProcOrderLineItem[];
  deliveries: ProcDelivery[];
  receipts: ProcReceiptSummary[];
  close_reason: string | null;
  cancel_reason: string | null;
}

export interface ProcImportResult {
  added: number;
  linked_onboarded: number;
  skipped_existing: number;
  ambiguous_ids: string[];
  batch_id: number;
  batch_name: string;
  skipped_ids: string[];
}

export interface ProcMintResult {
  batch_id: number;
  batch_name: string;
  minted: number;
  unique_ids: string[];
}

export interface ProcReceiptResult {
  receipt_id: number;
  receipt_no: string;
  received_ok: number;
  retired_bad: number;
  still_outstanding: number;
  effective_status: OrderEffStatus;
}

export interface ProcOnboardResult {
  procurement_device_id: number;
  unique_id: string;
  devices_main_id: number;
  device_guid: string | null;
  connectors_created: number;
}

export interface ProcOutstandingItem {
  order_id: number;
  order_no: string;
  manufacturer_name: string | null;
  procurement_device_id: number;
  unique_id: string;
  issued_on: string | null;
  days_outstanding: number | null;
}


export type Connectivity = "wifi" | "sim" | "lan" | "unknown";
export type SimStatus = "active" | "suspended" | "retired";

export interface ProcSim {
  id: number;
  msisdn: string;
  iccid: string | null;
  imsi: string | null;
  provider: string | null;
  plan: string | null;
  apn: string | null;
  status: SimStatus;
  notes: string | null;
  // current fitment (null when spare)
  device_id: number | null;
  device_unique_id: string | null;
  station_name: string | null;
  // derived from the latest recharge
  valid_until: string | null;
  days_left: number | null;
  last_recharge_on: string | null;
  last_recharge_amount: number | null;
  total_spent: number | null;
  recharge_count: number;
  created_at: string | null;
}

export interface ProcSimAssignment {
  id: number;
  sim_id: number;
  msisdn: string | null;
  procurement_device_id: number;
  device_unique_id: string | null;
  assigned_on: string | null;
  removed_on: string | null;
  reason: string | null;
  assigned_by: string | null;
}

export interface ProcRecharge {
  id: number;
  sim_id: number;
  recharged_on: string;
  amount: number | null;
  plan: string | null;
  validity_days: number | null;
  valid_until: string;
  reference: string | null;
  done_by: string | null;
  notes: string | null;
}

export interface ProcSimDetail extends ProcSim {
  assignments: ProcSimAssignment[];
  recharges: ProcRecharge[];
}

export interface ProcExpiringSim {
  sim_id: number;
  msisdn: string;
  provider: string | null;
  valid_until: string | null;
  days_left: number | null;
  device_id: number | null;
  device_unique_id: string | null;
  station_name: string | null;
  stage: string | null;
}

export interface ProcUnregisteredSim {
  procurement_device_id: number;
  unique_id: string;
  iccid: string | null;
  imsi: string | null;
  likely_provider: string | null;   // derived from the IMSI's MCC/MNC
  stage: string | null;
  connectivity: string | null;
  first_boot_at: string | null;
  cp_vendor: string | null;
  cp_model: string | null;
  serial_no: string | null;
  devices_main_id: number | null;
  display_name: string | null;
  station_name: string | null;
  org_name: string | null;
  current_state: string | null;
  last_active: string | null;
  is_online: boolean | null;
}

export const maintenanceApi = {
  // Stations
  getStations: (params?: Record<string, any>) =>
    axiosInstance.get<PaginatedResponse<MaintStation>>(`${BASE}/stations`, { params }),

  getStation: (guid: string) =>
    axiosInstance.get<MaintStation>(`${BASE}/stations/${guid}`),

  createStation: (data: {
    name: string;
    org_id: number;
    location_lat: number;
    location_lon: number;
    text_address?: string;
    station_logo_url?: string;
    is_active?: boolean;
    charging_station_type_id?: number;
    gst_state_code: number;
  }) => axiosInstance.post<MaintStation>(`${BASE}/stations`, data),

  updateStation: (guid: string, data: Record<string, any>) =>
    axiosInstance.put<MaintStation>(`${BASE}/stations/${guid}`, data),

  getStationSettings: (guid: string) =>
    axiosInstance.get<StationSettings>(`${BASE}/stations/${guid}/settings`),

  updateStationSettings: (
    guid: string,
    data: Partial<Omit<StationSettings, "charging_station_guid">>
  ) => axiosInstance.put<StationSettings>(`${BASE}/stations/${guid}/settings`, data),

  deleteStation: (guid: string) =>
    axiosInstance.delete(`${BASE}/stations/${guid}`),

  uploadStationLogo: (guid: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return axiosInstance.post<MaintStation>(`${BASE}/stations/${guid}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deleteStationLogo: (guid: string) =>
    axiosInstance.delete<MaintStation>(`${BASE}/stations/${guid}/logo`),

  getStationDevices: (guid: string) =>
    axiosInstance.get<MaintStationDevice[]>(`${BASE}/stations/${guid}/devices`),

  updateDevicePosition: (guid: string, deviceId: number, station_position: number) =>
    axiosInstance.put(`${BASE}/stations/${guid}/devices/${deviceId}/position`, { station_position }),

  getOrgs: () =>
    axiosInstance.get<OrgItem[]>(`${BASE}/stations/orgs`),

  getStationsDropdown: () =>
    axiosInstance.get<StationDropdownItem[]>(`${BASE}/stations/dropdown`),

  getStationTypes: () =>
    axiosInstance.get<StationTypeItem[]>(`${BASE}/stations/station-types`),

  getGstStates: () =>
    axiosInstance.get<GstStateItem[]>(`${BASE}/stations/gst-states`),

  // Devices
  getDevices: (params?: Record<string, any>) =>
    axiosInstance.get<PaginatedResponse<MaintDevice>>(`${BASE}/devices`, { params }),

  getDevice: (id: number) =>
    axiosInstance.get<MaintDeviceDetail>(`${BASE}/devices/${id}`),

  createDevice: (data: DeviceCreatePayload) =>
    axiosInstance.post(`${BASE}/devices`, data),

  updateDevice: (id: number, data: { unique_name?: string; display_name?: string }) =>
    axiosInstance.put(`${BASE}/devices/${id}`, data),

  uploadDeviceLogo: (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return axiosInstance.post(`${BASE}/devices/${id}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deleteDeviceLogo: (id: number) =>
    axiosInstance.delete(`${BASE}/devices/${id}/logo`),

  linkDeviceToStation: (id: number, data: { charging_station_id: number; station_position?: number }) =>
    axiosInstance.post(`${BASE}/devices/${id}/link-station`, data),

  unlinkDeviceFromStation: (id: number) =>
    axiosInstance.delete(`${BASE}/devices/${id}/link-station`),

  updateConnector: (deviceId: number, connectorId: number, data: { capacity_kwh?: number; socket_type_id?: number }) =>
    axiosInstance.put(`${BASE}/devices/${deviceId}/connectors/${connectorId}`, data),

  overrideConnectorStatus: (deviceId: number, connectorId: number, data: { status_name: string; notes?: string }) =>
    axiosInstance.post(`${BASE}/devices/${deviceId}/connectors/${connectorId}/override-status`, data),

  getSocketTypes: () =>
    axiosInstance.get<SocketTypeItem[]>(`${BASE}/devices/socket-types`),

  getConnectorStatuses: () =>
    axiosInstance.get<ConnectorStatusItem[]>(`${BASE}/devices/connector-statuses`),

  // Pricing
  getServiceTypes: () =>
    axiosInstance.get<ServiceTypeWithTaxes[]>(`${BASE}/pricing/service-types`),

  getStationPricingOverrides: (stationId: number) =>
    axiosInstance.get<PricingOverrideItem[]>(`${BASE}/pricing/station/${stationId}/overrides`),

  getOrgPricingOverrides: (orgId: number) =>
    axiosInstance.get<PricingOverrideItem[]>(`${BASE}/pricing/org/${orgId}/overrides`),

  getPricingOverride: (id: number) =>
    axiosInstance.get<PricingOverrideItem>(`${BASE}/pricing/overrides/${id}`),

  createPricingOverride: (data: PricingOverrideCreate) =>
    axiosInstance.post<PricingOverrideItem>(`${BASE}/pricing/overrides`, data),

  // Overrides are immutable + non-destructible (admin-api migration 016): the only
  // mutation is enable/disable. To change a price, disable and create a new override.
  setPricingOverrideActive: (id: number, is_active: boolean) =>
    axiosInstance.put<PricingOverrideItem>(`${BASE}/pricing/overrides/${id}`, { is_active }),

  // DELETE now disables server-side (kept for compatibility; prefer setPricingOverrideActive).
  deletePricingOverride: (id: number) =>
    axiosInstance.delete(`${BASE}/pricing/overrides/${id}`),

  getDeviceRate: (deviceId: number) =>
    axiosInstance.get<DeviceRateItem>(`${BASE}/pricing/device/${deviceId}/rate`),

  updateDeviceRate: (deviceId: number, data: { rate_kwh: number; service_type_key?: string }) =>
    axiosInstance.put<DeviceRateItem>(`${BASE}/pricing/device/${deviceId}/rate`, data),

  // Device Logs (historical)
  getNotificationTypes: () =>
    axiosInstance.get(`${BASE}/devices/notification-types`),

  getHistoricalLogs: (deviceId: number, params?: Record<string, any>) =>
    axiosInstance.get(`${BASE}/devices/${deviceId}/logs/history`, { params }),

  exportHistoricalLogs: (deviceId: number, params?: Record<string, any>) =>
    axiosInstance.get(`${BASE}/devices/${deviceId}/logs/export`, { params }),

  // Tenant-scoped device logs (historical) — for regular dashboard
  getHistoricalLogsTenant: (tenantId: string, deviceId: number, params?: Record<string, any>) =>
    axiosInstance.get(`/api/stations/tenants/${tenantId}/devices/${deviceId}/logs/history`, { params }),

  getNotificationTypesTenant: () =>
    axiosInstance.get(`/api/stations/notification-types`),

  // Audit
  getAuditLogs: (params?: Record<string, any>) =>
    axiosInstance.get<PaginatedResponse<AuditLogEntry>>(`${BASE}/audit`, { params }),

  // Remote OCPP commands
  getRemoteCommandCatalog: () =>
    axiosInstance.get<RemoteCommandCatalogEntry[]>(`${BASE}/remote-commands/catalog`),

  sendRemoteCommand: (deviceId: number, body: RemoteCommandRequest) =>
    axiosInstance.post<RemoteCommandResponse>(
      `${BASE}/remote-commands/${deviceId}`,
      body,
      // Browser default is 10s; OCPP round-trips can exceed that, especially reset/firmware.
      // Match the longest possible server-side per-action timeout (~60s) + slack.
      { timeout: 90_000 },
    ),

  // Vehicles — companies
  getVehicleCompanies: (params?: Record<string, any>) =>
    axiosInstance.get<PaginatedResponse<VehicleCompany>>(`${BASE}/vehicles/companies`, { params }),

  getVehicleCompaniesDropdown: () =>
    axiosInstance.get<VehicleCompanyOption[]>(`${BASE}/vehicles/companies/dropdown`),

  createVehicleCompany: (data: { name: string }) =>
    axiosInstance.post<VehicleCompany>(`${BASE}/vehicles/companies`, data),

  updateVehicleCompany: (id: number, data: { name: string }) =>
    axiosInstance.put<VehicleCompany>(`${BASE}/vehicles/companies/${id}`, data),

  deleteVehicleCompany: (id: number) =>
    axiosInstance.delete(`${BASE}/vehicles/companies/${id}`),

  // Vehicles — models
  getVehicleModels: (params?: Record<string, any>) =>
    axiosInstance.get<PaginatedResponse<VehicleModel>>(`${BASE}/vehicles/models`, { params }),

  createVehicleModel: (data: VehicleModelCreate) =>
    axiosInstance.post<VehicleModel>(`${BASE}/vehicles/models`, data),

  updateVehicleModel: (id: number, data: Partial<VehicleModelCreate>) =>
    axiosInstance.put<VehicleModel>(`${BASE}/vehicles/models/${id}`, data),

  deleteVehicleModel: (id: number) =>
    axiosInstance.delete(`${BASE}/vehicles/models/${id}`),

  // Vehicles — lookups (for the model form)
  getVehicleTypeOptions: () =>
    axiosInstance.get<VehicleTypeOption[]>(`${BASE}/vehicles/vehicle-types`),

  getVehicleSocketTypes: () =>
    axiosInstance.get<VehicleSocketType[]>(`${BASE}/vehicles/socket-types`),

  // Utilization (cross-tenant; excludes 'Test Private')
  getUtilizationOverview: (params?: Record<string, any>) =>
    axiosInstance.get<UtilOverviewResponse>(`${BASE}/utilization/overview`, { params }),

  getUtilizationStations: (params?: Record<string, any>) =>
    axiosInstance.get<PaginatedResponse<StationUtil>>(`${BASE}/utilization/stations`, { params }),

  getUtilizationDevices: (stationGuid: string, params?: Record<string, any>) =>
    axiosInstance.get<DeviceUtil[]>(`${BASE}/utilization/stations/${stationGuid}/devices`, { params }),

  getUtilizationConnectors: (deviceId: number, params?: Record<string, any>) =>
    axiosInstance.get<ConnectorUtil[]>(`${BASE}/utilization/devices/${deviceId}/connectors`, { params }),

  // Effective uptime over the window (reads device_connector_status_log)
  getServiceHealth: (params?: Record<string, any>) =>
    axiosInstance.get<ServiceHealth>(`${BASE}/utilization/health`, { params }),

  // Session-outcome health monitor (Redash Q29/Q30/Q48): station/device health + underperforming connectors
  getHealthMonitor: (params?: Record<string, any>) =>
    axiosInstance.get<HealthMonitor>(`${BASE}/utilization/health-monitor`, { params }),

  // Zitadel org/user provisioning (onboard an org to the public dashboard)
  zitadelGetProjectRoles: () =>
    axiosInstance.get<ZitadelProjectRole[]>(`${BASE}/zitadel/project/roles`),

  zitadelCreateOrg: (data: { name: string }) =>
    axiosInstance.post<ZitadelCreateOrgResp>(`${BASE}/zitadel/orgs`, data),

  zitadelCreateGrant: (orgId: string, data: { role_keys?: string[] }) =>
    axiosInstance.post<ZitadelGrantResp>(`${BASE}/zitadel/orgs/${orgId}/grant`, data),

  zitadelCreateUser: (orgId: string, data: ZitadelCreateUserReq) =>
    axiosInstance.post<ZitadelCreateUserResp>(`${BASE}/zitadel/orgs/${orgId}/users`, data),

  zitadelLinkTenant: (data: { org_name: string; zitadel_org_id: string }) =>
    axiosInstance.post<ZitadelLinkResp>(`${BASE}/zitadel/link-tenant`, data),

  // Zitadel — ongoing user management for already-provisioned orgs
  zitadelProvisionedOrgs: () =>
    axiosInstance.get<ZitadelProvisionedOrg[]>(`${BASE}/zitadel/provisioned-orgs`),

  zitadelListUsers: (orgId: string) =>
    axiosInstance.get<ZitadelUser[]>(`${BASE}/zitadel/orgs/${orgId}/users`),

  zitadelDeactivateUser: (orgId: string, userId: string) =>
    axiosInstance.post<ZitadelUserAction>(
      `${BASE}/zitadel/orgs/${orgId}/users/${userId}/deactivate`
    ),

  zitadelReactivateUser: (orgId: string, userId: string) =>
    axiosInstance.post<ZitadelUserAction>(
      `${BASE}/zitadel/orgs/${orgId}/users/${userId}/reactivate`
    ),

  // Zitadel — account recovery (2FA lock-outs, forgotten passwords)
  zitadelUserAuthFactors: (orgId: string, userId: string) =>
    axiosInstance.get<ZitadelAuthFactor[]>(
      `${BASE}/zitadel/orgs/${orgId}/users/${userId}/auth-factors`
    ),
  zitadelRemoveUserAuthFactor: (
    orgId: string,
    userId: string,
    factor: ZitadelAuthFactor["type"],
    tokenId?: string | null
  ) =>
    axiosInstance.delete<ZitadelRemoveFactorResp>(
      `${BASE}/zitadel/orgs/${orgId}/users/${userId}/auth-factors/${factor}`,
      { params: tokenId ? { token_id: tokenId } : undefined }
    ),
  zitadelSendPasswordReset: (orgId: string, userId: string) =>
    axiosInstance.post<{ user_id: string; sent: boolean }>(
      `${BASE}/zitadel/orgs/${orgId}/users/${userId}/password-reset`
    ),
  zitadelUpdateUserRoles: (
    orgId: string,
    userId: string,
    data: { role_keys: string[] }
  ) =>
    axiosInstance.put<ZitadelUserAction>(
      `${BASE}/zitadel/orgs/${orgId}/users/${userId}/roles`,
      data
    ),

  // Zitadel — org_custom + grant management (5-flow support)
  zitadelOrgTypes: () =>
    axiosInstance.get<ZitadelOrgType[]>(`${BASE}/zitadel/org-types`),

  zitadelUnlinkedOrgs: () =>
    axiosInstance.get<ZitadelUnlinkedOrg[]>(`${BASE}/zitadel/unlinked-orgs`),

  zitadelCreateOrgCustom: (data: {
    name: string;
    org_type: string;
    platform_url?: string;
  }) => axiosInstance.post<ZitadelOrgCustom>(`${BASE}/zitadel/org-custom`, data),

  zitadelGetGrant: (orgId: string) =>
    axiosInstance.get<ZitadelGrant>(`${BASE}/zitadel/orgs/${orgId}/grant`),

  zitadelUpdateGrant: (orgId: string, data: { role_keys: string[] }) =>
    axiosInstance.put<ZitadelGrant>(`${BASE}/zitadel/orgs/${orgId}/grant`, data),

  // --------------------------------------------------------------- Procurement
  // Manufacturers
  procGetManufacturers: (includeInactive = false) =>
    axiosInstance.get<ProcManufacturer[]>(`${BASE}/procurement/manufacturers`, {
      params: { include_inactive: includeInactive },
    }),

  procCreateManufacturer: (data: Partial<ProcManufacturer>) =>
    axiosInstance.post<ProcManufacturer>(`${BASE}/procurement/manufacturers`, data),

  procUpdateManufacturer: (id: number, data: Partial<ProcManufacturer>) =>
    axiosInstance.put<ProcManufacturer>(`${BASE}/procurement/manufacturers/${id}`, data),

  // Batches / mint / import
  procGetBatches: () => axiosInstance.get<ProcBatch[]>(`${BASE}/procurement/batches`),

  procMintBatch: (data: {
    quantity: number;
    name?: string;
    manufacturer_id?: number;
    hardware_model?: string;
    po_ref?: string;
    connector_std?: string;
    power_kw?: number;
    unit_cost?: number;
    notes?: string;
  }) => axiosInstance.post<ProcMintResult>(`${BASE}/procurement/batches`, data),

  /** Upload device_configuration.csv straight from the offline generator. */
  procImportCsv: (file: File, batchId?: number) => {
    const fd = new FormData();
    fd.append("file", file);
    return axiosInstance.post<ProcImportResult>(`${BASE}/procurement/import/csv`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      params: batchId ? { batch_id: batchId } : undefined,
    });
  },

  // Pool
  procGetSummary: () => axiosInstance.get<ProcPoolSummary>(`${BASE}/procurement/summary`),

  procGetDevices: (params?: {
    stage?: string;
    batch_id?: number;
    search?: string;
    page?: number;
    page_size?: number;
  }) => axiosInstance.get<PaginatedResponse<ProcDevice>>(`${BASE}/procurement/devices`, { params }),

  procGetDevice: (id: number) =>
    axiosInstance.get<ProcDeviceDetail>(`${BASE}/procurement/devices/${id}`),

  procUpdateDevice: (id: number, data: Record<string, any>) =>
    axiosInstance.put<ProcDeviceDetail>(`${BASE}/procurement/devices/${id}`, data),

  procRetireDevice: (id: number, reason: string) =>
    axiosInstance.post<ProcDeviceDetail>(`${BASE}/procurement/devices/${id}/retire`, { reason }),

  /** Identity is taken from the procurement record — never sent by the client. */
  procOnboardDevice: (id: number, data: {
    display_name?: string;
    ocpp_version?: string;
    station_id?: number;
    station_position?: number;
    connectors: { connector_name: string; socket_type_id?: number; capacity_kwh?: number }[];
  }) => axiosInstance.post<ProcOnboardResult>(`${BASE}/procurement/devices/${id}/onboard`, data),

  // Orders
  procGetOrders: (params?: {
    status?: string;
    manufacturer_id?: number;
    page?: number;
    page_size?: number;
  }) => axiosInstance.get<PaginatedResponse<ProcOrder>>(`${BASE}/procurement/orders`, { params }),

  procGetOrder: (id: number) =>
    axiosInstance.get<ProcOrderDetail>(`${BASE}/procurement/orders/${id}`),

  procCreateOrder: (data: { manufacturer_id: number; note?: string; device_ids: number[] }) =>
    axiosInstance.post<ProcOrderDetail>(`${BASE}/procurement/orders`, data),

  procUpdateOrderItems: (id: number, data: { add?: number[]; remove?: number[] }) =>
    axiosInstance.patch<ProcOrderDetail>(`${BASE}/procurement/orders/${id}/items`, data),

  /** The state change — dispatches every line item. Involves no email. */
  procIssueOrder: (id: number) =>
    axiosInstance.post<ProcOrderDetail>(`${BASE}/procurement/orders/${id}/issue`),

  procCancelOrder: (id: number, reason?: string) =>
    axiosInstance.post<ProcOrderDetail>(`${BASE}/procurement/orders/${id}/cancel`, { reason }),

  /** Short-close: write off units that are never arriving. */
  procCloseOrder: (id: number, reason: string) =>
    axiosInstance.post<ProcOrderDetail>(`${BASE}/procurement/orders/${id}/close`, { reason }),

  /** A delivery attempt. Failure is a logged row, never a blocked order. */
  procDeliverOrder: (id: number, data: { channel: string; target?: string; note?: string }) =>
    axiosInstance.post<ProcDelivery>(`${BASE}/procurement/orders/${id}/deliver`, data),

  /** The order's device list, fetched through axios so two things apply that a
   *  bare URL cannot get: the Authorization header the interceptor adds, and
   *  the API host in baseURL. This used to be a plain string handed to
   *  window.open, which is a navigation -- it carries no bearer token, and a
   *  relative path resolves against the console's own origin rather than the
   *  API's, so the request never reached admin-api at all. */
  /** Generate this order's CSV and QR label sheet, store both, record both.
   *  Safe to repeat: each run writes a new timestamped key rather than
   *  overwriting a file that may already be in someone's inbox. */
  procGenerateArtifacts: (id: number) =>
    axiosInstance.post<{ order_id: number; device_count: number; artifacts: ProcArtifact[] }>(
      `${BASE}/procurement/orders/${id}/artifacts`, {}, { timeout: 120000 }),

  procListArtifacts: (id: number) =>
    axiosInstance.get<{ order_id: number; artifacts: ProcArtifact[] }>(
      `${BASE}/procurement/orders/${id}/artifacts`),

  /** Streamed by the API after an auth check -- the bucket has website access
   *  disabled, so there is deliberately no URL to link to. */
  procDownloadArtifact: (artifactId: number) =>
    axiosInstance.get<Blob>(`${BASE}/procurement/artifacts/${artifactId}/download`, {
      responseType: "blob",
      timeout: 120000,
    }),

  procOrderCsv: (id: number) =>
    axiosInstance.get<Blob>(`${BASE}/procurement/orders/${id}/devices.csv`, {
      responseType: "blob",
    }),

  // Receipts
  procCreateReceipt: (orderId: number, data: {
    received_on?: string;
    challan_ref?: string;
    note?: string;
    lines: { procurement_device_id: number; condition: string; note?: string }[];
  }) => axiosInstance.post<ProcReceiptResult>(`${BASE}/procurement/orders/${orderId}/receipts`, data),

  procGetOutstanding: (agedDays = 0) =>
    axiosInstance.get<ProcOutstandingItem[]>(`${BASE}/procurement/outstanding`, {
      params: { aged_days: agedDays },
    }),

  procGetOrphans: (limit = 100) =>
    axiosInstance.get<any[]>(`${BASE}/procurement/orphans`, { params: { limit } }),


  // ------------------------------------------------------- Procurement: SIMs
  procGetSims: (params?: {
    search?: string;
    status?: string;
    unassigned_only?: boolean;
    page?: number;
    page_size?: number;
  }) => axiosInstance.get<PaginatedResponse<ProcSim>>(`${BASE}/procurement/sims`, { params }),

  procGetSim: (id: number) =>
    axiosInstance.get<ProcSimDetail>(`${BASE}/procurement/sims/${id}`),

  procCreateSim: (data: Partial<ProcSim>) =>
    axiosInstance.post<ProcSimDetail>(`${BASE}/procurement/sims`, data),

  procUpdateSim: (id: number, data: Partial<ProcSim>) =>
    axiosInstance.put<ProcSimDetail>(`${BASE}/procurement/sims/${id}`, data),

  procAssignSim: (id: number, data: { procurement_device_id: number; reason?: string }) =>
    axiosInstance.post<ProcSimDetail>(`${BASE}/procurement/sims/${id}/assign`, data),

  procUnassignSim: (id: number, reason?: string) =>
    axiosInstance.post<ProcSimDetail>(`${BASE}/procurement/sims/${id}/unassign`, { reason }),

  /** Latest recharge IS the live expiry — pass valid_until or validity_days. */
  procAddRecharge: (id: number, data: {
    recharged_on?: string;
    amount?: number;
    plan?: string;
    validity_days?: number;
    valid_until?: string;
    reference?: string;
    notes?: string;
  }) => axiosInstance.post<ProcSimDetail>(`${BASE}/procurement/sims/${id}/recharges`, data),

  /** Plans running out — i.e. the chargers about to drop off the network. */
  procGetExpiringSims: (days = 7, includeExpired = true) =>
    axiosInstance.get<ProcExpiringSim[]>(`${BASE}/procurement/sims/expiring`, {
      params: { days, include_expired: includeExpired },
    }),

  procSetConnectivity: (pdId: number, connectivity: Connectivity) =>
    axiosInstance.put<ProcDeviceDetail>(`${BASE}/procurement/devices/${pdId}/connectivity`, {
      connectivity,
    }),

  /** Chargers reporting an ICCID we hold no SIM record for — OCPP never sends the number. */
  procGetUnregisteredSims: () =>
    axiosInstance.get<ProcUnregisteredSim[]>(`${BASE}/procurement/sims/unregistered`),

};

// ============================================================================
// Remote OCPP command types
// ============================================================================

export type RemoteCommandFieldType =
  | "string"
  | "int"
  | "number"
  | "datetime"
  | "bool"
  | "enum"
  | "enum_multi"
  | "json"
  | "url"
  | "table";

export interface RemoteCommandField {
  name: string;
  label: string;
  type: RemoteCommandFieldType;
  required: boolean;
  default?: unknown;
  placeholder?: string | null;
  help?: string | null;
  options?: string[] | null;
  suggestions?: string[] | null;
  min?: number | null;
  max?: number | null;
  fields?: RemoteCommandField[] | null;
}

export interface RemoteCommandCatalogEntry {
  action: string;
  title: string;
  group: string;
  description: string;
  destructive: boolean;
  confirm_text: string | null;
  timeout_seconds: number;
  fields: RemoteCommandField[];
}

export interface RemoteCommandRequest {
  action: string;
  meta: Record<string, unknown>;
  timeout_seconds?: number;
}

export interface RemoteCommandResponse {
  status: "ok" | "device_error";
  action: string;
  int_event_id?: string | null;
  latency_ms?: number | null;
  response: Record<string, unknown>;
}

// ============================================================================
// Zitadel provisioning types
// ============================================================================

export interface ZitadelProjectRole {
  key: string;
  display_name?: string | null;
  group?: string | null;
}

export interface ZitadelCreateOrgResp {
  org_id: string;
  name: string;
}

export interface ZitadelGrantResp {
  org_id: string;
  project_id: string;
  grant_id: string;
  role_keys: string[];
  already_existed: boolean;
}

export interface ZitadelCreateUserReq {
  email: string;
  first_name: string;
  last_name: string;
  username?: string;
  initial_password?: string;
  role_keys?: string[];
  send_email?: boolean;
  login_url?: string;
  org_name?: string;
}

export interface ZitadelCreateUserResp {
  org_id: string;
  user_id: string;
  username: string;
  email: string;
  initial_password: string;
  user_grant_id: string;
  role_keys: string[];
  email_sent: boolean;
  email_error?: string | null;
}

export interface ZitadelLinkResp {
  org_custom_id: number;
  org_name: string;
  zitadel_tenant_id: string;
}

export interface ZitadelProvisionedOrg {
  org_custom_id: number;
  name: string;
  zitadel_tenant_id: string;
  platform_url?: string | null;
}

export interface ZitadelUser {
  user_id: string;
  username: string;
  email?: string | null;
  display_name?: string | null;
  state: string;
  role_keys: string[];
  grant_id?: string | null;
}

export interface ZitadelAuthFactor {
  type: "totp" | "otp_sms" | "otp_email" | "u2f";
  state: string;
  ready: boolean;
  id?: string | null;
  name?: string | null;
}

export interface ZitadelRemoveFactorResp {
  user_id: string;
  removed: string;
  token_id?: string | null;
}

export interface ZitadelUserAction {
  user_id: string;
  state: string;
  role_keys: string[];
}

export interface ZitadelOrgType {
  id: number;
  org_type: string;
  display_name: string;
}

export interface ZitadelUnlinkedOrg {
  org_custom_id: number;
  name: string;
  platform_url?: string | null;
}

export interface ZitadelOrgCustom {
  org_custom_id: number;
  name: string;
  zitadel_tenant_id?: string | null;
  platform_url?: string | null;
}

export interface ZitadelGrant {
  org_id: string;
  project_id: string;
  grant_id?: string | null;
  role_keys: string[];
}

// ============================================================================
// Pricing coverage — device-rate-removal readiness (pricing-configs page)
// ============================================================================
export interface CoverageOrgRow {
  org_id: number;
  org_name: string;
  sessions: number;
  status: "stable" | "fragile" | "broken";
}
export interface SuggestedOverride {
  kind: "public_default" | "org_private" | "pin_default";
  org_id: number;
  org_name: string;
  suggested_price: number | null;
  reason: string;
}
export interface StationCoverage {
  station_id: number;
  station_name: string;
  station_type: string | null; // public | private | semi-private
  operator_org_id: number;
  operator_org_name: string;
  device_rate: number | null; // current fallback = suggested public price
  has_public_default: boolean;
  total_sessions: number;
  status: "covered" | "gaps";
  orgs: CoverageOrgRow[];
  suggestions: SuggestedOverride[];
}
export interface PricingCoverageResponse {
  total_stations: number;
  fully_covered: number;
  with_gaps: number;
  stations: StationCoverage[];
}

/** Live coverage for removing the device-rate fallback; drives the pricing-configs gaps view. */
export const getPricingCoverage = () =>
  axiosInstance
    .get<PricingCoverageResponse>("api/maintenance/pricing/coverage")
    .then((r) => r.data);
